import { apiDelete, apiPatch, apiPost } from '@/features/shared/utils/dataFetching'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type ValidAddress,
  type BaseAddress,
  type DeleteAddressResponse,
  type AddressMutationBffResponse,
} from '@/features/account/services/Addresses/types'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { userIsDoNotShareSellTA } from '@/features/account/services/DataPrivacy/utils'
import { setClientOptOutState } from '@/features/account/services/DataPrivacy/fetchers'

// The BFF expects delivery detail fields nested under `details`;
// the app model keeps them flat. Always sent so empty values clear them.
const toBffAddressPayload = <
  T extends {
    property_type?: string
    requested_delivery_location?: string
    shopper_arrive?: string[]
    gate_code?: string
  },
>({
  property_type,
  requested_delivery_location,
  shopper_arrive,
  gate_code,
  ...address
}: T) => ({
  ...address,
  details: {
    property_type,
    requested_delivery_location,
    shopper_arrive,
    gate_code,
  },
})

const onOptOutSuccessHandler =
  (queryClient: QueryClient) =>
  async ({ user_opt_out }: { user_opt_out: boolean }) => {
    // BACKEND took care of opting out the user, FE needs to invalidate queries,  set window property and call opt-out endpoint to set cookies
    //
    if (user_opt_out) {
      await setClientOptOutState()
      return queryClient.invalidateQueries()
    }

    // no user opt out, so just refetch the user query
    return queryClient.invalidateQueries({
      queryKey: userQueryOptions.queryKey,
    })
  }

export const useMutationAddAddress = () => {
  const queryClient = useQueryClient()

  const checkUserOptOut = !userIsDoNotShareSellTA()
  return useMutation({
    mutationFn: async ({
      address,
      overrideGroupId,
    }: {
      address: ValidAddress
      overrideGroupId?: string
    }) => {
      const resp = await apiPost<AddressMutationBffResponse>({
        config: {
          url: 'member-web-bff/v1/address',
          data: {
            address: toBffAddressPayload(address),
            check_user_opt_out: checkUserOptOut,
          },
        },
        options: {
          ignoredMessages: [
            /address not in service area/i,
            'delivery instructions contains invalid or unsafe content',
            'delivery instructions must be 400 characters or less',
            'Please enter a valid address',
            'street1 must be present',
            "too many addresses, you're allowed a maximum of 100",
          ],
          overrideGroupId,
        },
        fetcherName: 'useMutationAddAddressBff',
        tag: 'bff',
      })

      // satisfy typescript, as there are consumers that expect address
      // to be returned from this mutation
      return { ...resp.address, user_opt_out: resp.user_opt_out }
    },
    onSuccess: onOptOutSuccessHandler(queryClient),
  })
}
export const useMutationUpdateAddress = <TAddress extends Partial<BaseAddress>>() => {
  const queryClient = useQueryClient()
  // if user is not already marked as do not share, tell backend to check
  const checkUserOptOut = !userIsDoNotShareSellTA()
  return useMutation({
    mutationFn: (address: TAddress) =>
      apiPatch<AddressMutationBffResponse>({
        config: {
          url: `member-web-bff/v1/address/${address.id}`,
          data: {
            address: toBffAddressPayload(address),
            check_user_opt_out: checkUserOptOut,
          },
        },
        options: {
          ignoredMessages: [
            /address not in service area/i,
            'delivery instructions contains invalid or unsafe content',
            'delivery instructions must be 400 characters or less',
          ],
        },
        fetcherName: 'useMutationUpdateAddressBff',
        tag: 'bff',
      }),
    onSuccess: onOptOutSuccessHandler(queryClient),
  })
}

export const useMutationDeleteAddress = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (addressId: number) =>
      apiDelete<DeleteAddressResponse>({
        config: {
          url: `api/v1/customer_addresses/${addressId}`,
        },
        fetcherName: 'useMutationDeleteAddress',
      }),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: userQueryOptions.queryKey,
      })
    },
  })
}
